import DiagnosticMonitorPage from "../DiagnosticMonitorPage";
import { DEMO_PRESET } from "../hooks/useDemoData";
import { DemoQueryClientProvider } from "./DemoQueryClientProvider";
import { DataGridSettingsProvider } from "@/components/DataGridSettingsProvider";
import { ModalProvider } from "@/components/ModalProvider";
import { PresetProvider } from "@/components/PresetProvider";
import { isViewModeActive, ViewProvider } from "@/components/ViewProvider";
import AddInIds from "@/constants/AddInIds";
import { PageType, presetSelectionKey } from "@/constants/PageType";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useDefaultPreset } from "@/hooks/presets/useDefaultPreset";
import { useDiagnosticViews } from "@/hooks/views/useDiagnosticViews";
import { useViewMigration } from "@/hooks/views/useViewMigration";
import { Preset } from "@/models/Preset";
import isDemo from "@/utilities/isDemo";

function ViewModeShell({ pageType, children }: { pageType: PageType; children: React.ReactNode }) {
  return (
    <PresetProvider selectionKey={presetSelectionKey(pageType)}>
      <ViewProvider pageType={pageType}>
        <DataGridSettingsProvider pageType={pageType}>
          <ModalProvider>{children}</ModalProvider>
        </DataGridSettingsProvider>
      </ViewProvider>
    </PresetProvider>
  );
}

function LegacyShell({
  pageType,
  children,
  fallbackPresets,
}: {
  pageType: PageType;
  children: React.ReactNode;
  /** Shown instead of the stored presets, e.g. a default preset that could not be stored. */
  fallbackPresets?: Preset[];
}) {
  return (
    <PresetProvider
      selectionKey={presetSelectionKey(pageType)}
      initialPresets={isDemo ? [DEMO_PRESET] : fallbackPresets}
    >
      <DataGridSettingsProvider pageType={pageType}>
        <ModalProvider>{children}</ModalProvider>
      </DataGridSettingsProvider>
    </PresetProvider>
  );
}

function ShellDispatcher({ pageType }: { pageType: PageType }) {
  useViewMigration();
  const {
    data: views = [],
    isLoading: viewsLoading,
    isPlaceholderData: viewsPlaceholder,
  } = useDiagnosticViews();
  const {
    data: presets = [],
    isLoading: presetsLoading,
    isPlaceholderData: presetsPlaceholder,
  } = useAddinData<Preset>(AddInIds.presets);

  // useAddinData starts with an empty placeholder, so only a settled empty list means "none".
  const needsDefaultPreset =
    !viewsPlaceholder && !presetsPlaceholder && views.length === 0 && presets.length === 0;
  const { data: defaultPreset, isPending: defaultPresetPending } =
    useDefaultPreset(needsDefaultPreset);

  if (viewsLoading || presetsLoading) return null;
  if (needsDefaultPreset && defaultPresetPending) return null;

  const migrationInFlight = sessionStorage.getItem("diagViewsMigrating") === "1";
  const migrationPending = views.length === 0 && presets.length > 0;

  if (migrationInFlight || migrationPending) return null;

  if (isViewModeActive(views)) {
    return (
      <ViewModeShell pageType={pageType}>
        <DiagnosticMonitorPage />
      </ViewModeShell>
    );
  }
  const unstoredDefault =
    needsDefaultPreset && defaultPreset && !defaultPreset.stored ? [defaultPreset.preset] : undefined;
  return (
    <LegacyShell pageType={pageType} fallbackPresets={unstoredDefault}>
      <DiagnosticMonitorPage />
    </LegacyShell>
  );
}

export function DiagnosticMonitorPageWrapper(props: { pageType: PageType }) {
  if (isDemo) {
    return (
      <DemoQueryClientProvider>
        <LegacyShell pageType={props.pageType}>
          <DiagnosticMonitorPage />
        </LegacyShell>
      </DemoQueryClientProvider>
    );
  }
  return <ShellDispatcher pageType={props.pageType} />;
}
